import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Loader2, Search, X } from "lucide-react";
import SearchFooter from "./global-search/search-footer";
import SearchResults from "./global-search/search-results";
import { hrefFor, SearchItem, SearchPage } from "./global-search/types";
import { useSearchKeyboard } from "./global-search/use-search-keyboard";
import { useSearchResults } from "./global-search/use-search-results";

export type { SearchPage };

/**
 * Global search palette (demo-ui GlobalSearch) over the backend's GET /search
 * grouped endpoint: Pages (client-side navigation), Leads, Deals and
 * Recordings. Every row is a real link (see ResultRow), so ⌘/Ctrl+click and
 * "Open in new tab" work; a plain click stays an in-app navigation.
 */

type Props = {
  open: boolean;
  onClose: () => void;
  pages: SearchPage[];
};

const GlobalSearch = ({ open, onClose, pages }: Props) => {
  const navigate = useNavigate();
  const listRef = useRef<HTMLDivElement>(null);
  const results = useSearchResults(open, pages);
  const { query, setQuery, trimmed, loading, items, leads, deals, recordings } = results;

  const selectItem = useCallback(
    (item: SearchItem) => {
      onClose();
      navigate(hrefFor(item));
    },
    [navigate, onClose]
  );

  const { activeIdx, setActiveIdx } = useSearchKeyboard({
    open,
    items,
    resetKey: `${trimmed}|${leads.items.length}|${deals.items.length}|${recordings.items.length}`,
    listRef,
    onSelect: selectItem,
    onClose,
  });

  // Body scroll lock
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[140] flex items-start justify-center overflow-y-auto px-4 pt-[10vh]">
      <div className="fixed inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
      <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-white/[0.08] bg-gradient-to-b from-ink-800/95 to-ink-900/95 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.7)] backdrop-blur-xl animate-fade-in-up-fast">
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-brand-500/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent-purple/15 blur-3xl" />

        {/* Input */}
        <div className="relative flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
          <Search className="h-4 w-4 shrink-0 text-slate-400" />
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search leads, deals, recordings or jump to a page..."
            className="h-full flex-1 bg-transparent text-[15px] text-white placeholder:text-slate-500 focus:outline-none"
          />
          {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-slate-500" />}
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              aria-label="Clear"
              className="grid h-6 w-6 place-items-center rounded-md text-slate-500 transition hover:bg-white/[0.04] hover:text-white"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <kbd className="hidden items-center gap-1 rounded-md border border-white/[0.08] bg-white/[0.04] px-1.5 py-0.5 text-[10px] font-medium text-slate-400 md:inline-flex">
            ESC
          </kbd>
        </div>

        <div ref={listRef} className="relative max-h-[55vh] overflow-y-auto py-2">
          <SearchResults results={results} activeIdx={activeIdx} setActiveIdx={setActiveIdx} onSelect={selectItem} />
        </div>

        <SearchFooter />
      </div>
    </div>,
    document.body
  );
};

export default GlobalSearch;
